import { Container } from "@mantine/core";
// import { Container } from "@mantine/core";
import {
  Text,
  Card,
  Paper,
  Button,
  Grid,
  Badge,
  Group,
  Stack,
  Title,
  Box,
  Flex,
  Avatar,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import { NonPaginatedResponse } from "../../Lib/responses";
import { LessonProps } from "../../Lib/types";
import { Endpoints } from "../../Lib/endpoints";
import { Calendar } from "@mantine/dates";
import {
  IconClock,
  IconCalendar,
  IconBook,
  IconArrowRight,
  IconPlayerPlay,
  IconChevronDown,
  IconChevronUp,
  IconCalendarOff,
  IconCoffee,
  IconMessageCircle,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import { Colors } from "../../Lib/data";
import MegaLoader from "../../Components/MegaLoader";
import { motion } from "framer-motion";
import { AppContext } from "../Dashboard";

interface LessonPropsWithCourseTitle extends LessonProps {
  courseTitle: string;
}

const Overview: React.FC = () => {
  const appContext = useContext(AppContext);
  const isEnrolled = appContext?.profile && appContext?.profile?.enrolledPlans?.length > 0;
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [showAllPast, setShowAllPast] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { t } = useTranslation();

  const { data: calendar, isLoading: isLoadingCalendar } = usePerformRequest<
    NonPaginatedResponse<LessonPropsWithCourseTitle[]>
  >({
    method: Endpoints.GetLessonsForCalendar().method,
    route: Endpoints.GetLessonsForCalendar().route,
  });

  const lessons = calendar?.data || [];

  // Separate upcoming and past lessons
  const { upcomingLessons, pastLessons, lessonDates } = useMemo(() => {
    const now = Date.now(); // Using timestamp for comparison
    const upcoming: LessonPropsWithCourseTitle[] = [];
    const past: LessonPropsWithCourseTitle[] = [];
    const dates: Date[] = [];

    lessons.forEach((lesson) => {
      const lessonDate = new Date(lesson.initialRunTime);
      dates.push(lessonDate);

      // Use initialRunTime to determine if lesson is upcoming
      const isUpcoming = lesson.initialRunTime > now;

      if (isUpcoming) {
        upcoming.push(lesson);
      } else {
        past.push(lesson);
      }
    });

    // Sort upcoming by initialRunTime (earliest first)
    upcoming.sort((a, b) => a.initialRunTime - b.initialRunTime);

    // Sort past by initialRunTime (most recent first)
    past.sort((a, b) => b.initialRunTime - a.initialRunTime);

    return { upcomingLessons: upcoming, pastLessons: past, lessonDates: dates };
  }, [lessons]);

  // Get lessons for selected date
  const selectedDateLessons = useMemo(() => {
    if (!selectedDate) return [];

    return lessons.filter((lesson) => {
      const lessonDate = new Date(lesson.initialRunTime);
      return lessonDate.toDateString() === selectedDate.toDateString();
    });
  }, [lessons, selectedDate]);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatSelectedDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderLessonCard = (lesson: LessonPropsWithCourseTitle, isUpcoming: boolean = true) => (
    <Card key={lesson.id} shadow="sm" padding="lg" radius="md" withBorder className="lesson-card">
      <Group justify="space-between" mb="xs">
        <Group>
          <Avatar size="sm" radius="xl" color={isUpcoming ? "blue" : "gray"}>
            <IconBook size={16} />
          </Avatar>
          <div>
            <Text fw={500} size="sm">
              {lesson.title}
            </Text>
            <div className="flex-row align-center">
              <Text size="xs" c="dimmed" mr={5}>
                {t("overview.courseTitle")}:
              </Text>
              <Link to={`/d/course/${lesson.courseID}`}>
                <Text size="xs" c="dimmed" td="underline">
                  {lesson.courseTitle}
                </Text>
              </Link>
            </div>
          </div>
        </Group>
        <Badge color={isUpcoming ? "blue" : "gray"} variant="light">
          {isUpcoming ? t("overview.upcoming") : t("overview.completed")}
        </Badge>
      </Group>

      <Group mb="md">
        <Group gap={4}>
          <IconCalendar size={14} />
          <Text size="xs" c="dimmed">
            {formatDate(lesson.initialRunTime)}
          </Text>
        </Group>
        {lesson.runTimes && lesson.runTimes.length > 0 && (
          <Group gap={4}>
            <IconClock size={14} />
            <Text size="xs" c="dimmed">
              {Math.round(lesson.runTimes[0] / 60)} min
            </Text>
          </Group>
        )}
      </Group>

      {lesson.description && (
        <Text size="sm" c="dimmed" mb="md" lineClamp={2}>
          {lesson.description}
        </Text>
      )}

      <Group justify="space-between" mt="md">
        <Button
          variant="light"
          color={isUpcoming ? "blue" : "gray"}
          size="xs"
          leftSection={isUpcoming ? <IconPlayerPlay size={14} /> : <IconBook size={14} />}
          component={Link}
          to={`/d/lesson/${lesson.id}`}
        >
          {isUpcoming ? t("overview.joinLesson") : t("overview.review")}
        </Button>
        <Tooltip label={t("overview.viewCourse")}>
          <ActionIcon
            variant="subtle"
            color={isUpcoming ? "blue" : "gray"}
            component={Link}
            to={`/d/course/${lesson.courseID}`}
          >
            <IconArrowRight size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>
    </Card>
  );

  const renderNoLessonsCard = () => (
    <Card
      shadow="sm"
      padding="xl"
      radius="lg"
      withBorder
      className="no-lessons-card"
      style={{
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
        border: "2px dashed #cbd5e1",
        textAlign: "center",
      }}
    >
      <Stack align="center" gap="md">
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <IconCalendarOff size={40} color="#64748b" />
        </div>

        <div>
          <Title order={3} c="dimmed" mb="xs">
            {t("overview.noLessonsToday")}
          </Title>
          <Text size="sm" c="dimmed" mb="lg">
            {formatSelectedDate(selectedDate!)} {t("overview.freeDay")}
          </Text>
        </div>

        <Group gap="sm">
          <IconCoffee size={20} color="#64748b" />
          <Text size="sm" c="dimmed">
            {t("overview.perfectTime")}
          </Text>
        </Group>

        <Button
          variant="subtle"
          color="gray"
          size="sm"
          mt="md"
          onClick={() => setSelectedDate(null)}
        >
          {t("overview.viewAllLessons")}
        </Button>
      </Stack>
    </Card>
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 1 }}
        animate={{
          opacity: isLoadingCalendar ? 1 : 0,
          pointerEvents: isLoadingCalendar ? "auto" : "none",
        }}
        transition={{ duration: 0.5 }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "linear-gradient(120deg, #18122B 60%, #a78bfa 100%)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MegaLoader size={64} color="violet" type="bars" />
      </motion.div>
      <div
        style={{
          opacity: isLoadingCalendar ? 0 : 1,
          transition: "opacity 0.5s",
        }}
      >
        <Container size="xl" className="overview-container">
          <div className="component-header">
            <Title order={2} mb="lg">
              {t("overview.title")}
            </Title>
          </div>

          <Grid>
            {/* Calendar Section */}
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Paper shadow="sm" p="lg" radius="md" withBorder>
                <div className="flex-col align-center width-100">
                  <Title order={4} mb="md" c={Colors.PurplePrimary}>
                    <Group>
                      <IconCalendar size={24} />
                      {t("overview.learningCalendar")}
                    </Group>
                  </Title>
                  <Calendar
                    size="md"
                    renderDay={(date) => {
                      const dateObj = new Date(date);
                      const hasLesson = lessonDates.some(
                        (lessonDate) => lessonDate.toDateString() === dateObj.toDateString()
                      );

                      // Count lessons on this day for different styling
                      const lessonsOnDay = lessonDates.filter(
                        (lessonDate) => lessonDate.toDateString() === dateObj.toDateString()
                      ).length;

                      // Check if this date is selected
                      const isSelected =
                        selectedDate && selectedDate.toDateString() === dateObj.toDateString();

                      return (
                        <Tooltip
                          label={
                            hasLesson
                              ? `${lessonsOnDay} lesson${lessonsOnDay > 1 ? "s" : ""} on this day`
                              : ""
                          }
                          disabled={!hasLesson}
                          withArrow
                          position="top"
                        >
                          <div
                            className={`calendar-day-wrapper ${hasLesson ? "has-lesson" : ""} ${
                              isSelected ? "selected" : ""
                            }`}
                            style={{
                              position: "relative",
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "6px",
                              background: isSelected
                                ? "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)"
                                : hasLesson
                                ? lessonsOnDay > 1
                                  ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                  : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                                : "transparent",
                              transition: "all 0.2s ease",
                              boxShadow: isSelected
                                ? "0 4px 12px rgba(255, 107, 107, 0.4)"
                                : hasLesson
                                ? "0 2px 8px rgba(99, 102, 241, 0.3)"
                                : "none",
                              cursor: "pointer",
                            }}
                            onClick={() => setSelectedDate(dateObj)}
                          >
                            <span
                              style={{
                                position: "relative",
                                fontWeight: isSelected || hasLesson ? 600 : 400,
                                color: isSelected || hasLesson ? "#ffffff" : "inherit",
                                fontSize: isSelected || hasLesson ? "14px" : "13px",
                              }}
                            >
                              {dateObj.getDate()}
                            </span>

                            {/* Simple lesson count indicator for multiple lessons */}
                            {lessonsOnDay > 1 && !isSelected && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "-3px",
                                  right: "-3px",
                                  width: "8px",
                                  height: "8px",
                                  background: "#ff6b6b",
                                  borderRadius: "50%",
                                  fontSize: "7px",
                                  fontWeight: "bold",
                                  color: "white",
                                  border: "1px solid white",
                                }}
                              />
                            )}
                          </div>
                        </Tooltip>
                      );
                    }}
                  />
                </div>
              </Paper>
            </Grid.Col>

            {/* Lessons Section */}
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="lg">
                {/* Selected Date Lessons */}
                {selectedDate && (
                  <Paper shadow="sm" p="lg" radius="md" withBorder>
                    <Flex justify="space-between" align="center" mb="md">
                      <Title order={3}>
                        <Group>
                          <IconCalendar size={24} />
                          {t("overview.lessonsFor")} {formatSelectedDate(selectedDate)}
                        </Group>
                      </Title>
                      <Badge color="purple" variant="filled">
                        {selectedDateLessons.length}
                      </Badge>
                    </Flex>

                    {selectedDateLessons.length > 0 ? (
                      <Stack gap="md">
                        {selectedDateLessons.map((lesson) => {
                          const isUpcoming = lesson.initialRunTime > Date.now();
                          return renderLessonCard(lesson, isUpcoming);
                        })}
                      </Stack>
                    ) : (
                      renderNoLessonsCard()
                    )}
                  </Paper>
                )}
                {appContext?.isLoadingProfile ? (
                  <>
                    <MegaLoader />
                  </>
                ) : (
                  <>
                    {/* Upcoming Lessons */}
                    {!isEnrolled ? (
                      <Paper shadow="sm" p="xl" radius="lg" withBorder>
                        <Stack align="center" gap="lg">
                          <div
                            style={{
                              width: "100px",
                              height: "100px",
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: "16px",
                            }}
                          >
                            <IconBook size={50} color="#ffffff" />
                          </div>

                          <div style={{ textAlign: "center" }}>
                            <Title order={2} mb="md" c="dimmed">
                              {t("overview.notEnrolled.title")}
                            </Title>
                            <Text size="lg" c="dimmed" mb="lg" style={{ maxWidth: "500px" }}>
                              {t("overview.notEnrolled.description")}
                            </Text>
                          </div>

                          <Group gap="md">
                            <Button
                              variant="filled"
                              size="lg"
                              component={Link}
                              to="/plans"
                              leftSection={<IconArrowRight size={20} />}
                              style={{
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                border: "none",
                                borderRadius: "12px",
                                padding: "12px 32px",
                                fontWeight: 600,
                                boxShadow: "0 8px 20px rgba(102, 126, 234, 0.3)",
                              }}
                            >
                              {t("overview.notEnrolled.browsePlans")}
                            </Button>
                          </Group>

                          <Text size="sm" c="dimmed" ta="center" style={{ maxWidth: "400px" }}>
                            {t("overview.notEnrolled.footer")}
                          </Text>
                        </Stack>
                      </Paper>
                    ) : (
                      <>
                        <Paper shadow="sm" p="lg" radius="md" withBorder>
                          <Flex justify="space-between" align="center" mb="md">
                            <Title order={3}>
                              <Group>
                                <IconClock size={24} />
                                {t("overview.upcomingLessons")}
                              </Group>
                            </Title>
                            <Badge color="blue" variant="filled">
                              {upcomingLessons.length}
                            </Badge>
                          </Flex>

                          {upcomingLessons.length > 0 ? (
                            <Stack gap="md">
                              {showAllUpcoming
                                ? upcomingLessons.map((lesson) => renderLessonCard(lesson, true))
                                : upcomingLessons
                                    .slice(0, 3)
                                    .map((lesson) => renderLessonCard(lesson, true))}
                              {upcomingLessons.length > 3 && (
                                <Button
                                  variant="subtle"
                                  fullWidth
                                  onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                                  leftSection={
                                    showAllUpcoming ? (
                                      <IconChevronUp size={16} />
                                    ) : (
                                      <IconChevronDown size={16} />
                                    )
                                  }
                                >
                                  {showAllUpcoming
                                    ? t("overview.showLess")
                                    : t("overview.viewMoreUpcoming", {
                                        count: upcomingLessons.length - 3,
                                      })}
                                </Button>
                              )}
                            </Stack>
                          ) : (
                            <Stack gap="md">
                              <Text c="dimmed" ta="center" py="xl">
                                {t("overview.noUpcomingLessons")}
                              </Text>
                            </Stack>
                          )}
                        </Paper>

                        {/* Recent/Past Lessons */}
                        <Paper shadow="sm" p="lg" radius="md" withBorder>
                          <Flex justify="space-between" align="center" mb="md">
                            <Title order={3}>
                              <Group>
                                <IconBook size={24} />
                                {t("overview.recentLessons")}
                              </Group>
                            </Title>
                            <Badge color="gray" variant="filled">
                              {pastLessons.length}
                            </Badge>
                          </Flex>

                          {pastLessons.length > 0 ? (
                            <Stack gap="md">
                              {showAllPast
                                ? pastLessons.map((lesson) => renderLessonCard(lesson, false))
                                : pastLessons
                                    .slice(0, 3)
                                    .map((lesson) => renderLessonCard(lesson, false))}
                              {pastLessons.length > 3 && (
                                <Button
                                  variant="subtle"
                                  fullWidth
                                  onClick={() => setShowAllPast(!showAllPast)}
                                  leftSection={
                                    showAllPast ? (
                                      <IconChevronUp size={16} />
                                    ) : (
                                      <IconChevronDown size={16} />
                                    )
                                  }
                                >
                                  {showAllPast
                                    ? t("overview.showLess")
                                    : t("overview.viewMoreCompleted", {
                                        count: pastLessons.length - 3,
                                      })}
                                </Button>
                              )}
                            </Stack>
                          ) : (
                            <Text c="dimmed" ta="center" py="xl">
                              {t("overview.noCompletedLessons")}
                            </Text>
                          )}
                        </Paper>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Grid.Col>
          </Grid>
        </Container>
      </div>
    </>
  );
};

export default Overview;
